import { CacheKey } from "@/common/stores/persistingStores/app";
import {
	CommonChatMessage,
	DisplayMessageType,
	ExtendedChatMessage,
	ExtendedCustomMessage,
	MessagingMethods,
} from "@/components/molecules/interactionWorkspace";
import { useChatStyle } from "@/components/molecules/interactionWorkspace/content/messagingInteractionContent/style";
import { useIntl } from "react-intl";
import { useInfiniteQueryHook } from "../infiniteQuery";
import { useEffect, useMemo } from "react";
import { Spinner } from "@fluentui/react-components";
import {
	ChatMessage,
	CustomMessage,
	MessageProps,
	MessageRenderer,
	SystemMessage,
} from "@azure/communication-react";
import dayjs from "dayjs";
import { WebchatDefaultCustomerName } from "@/common/constants";
import {
	fetchTranscription,
	TranscriptionMessagesKey,
} from "@/repository/queryFns/transcription";

export const useTranscriptionMessaging = (
	interactionId: string,
	date: string | undefined
): MessagingMethods => {
	const [
		transcriptionMessagesResult,
		,
		transcriptionMessagesInView,
		setFilter,
	] = useInfiniteQueryHook<CommonChatMessage>(
		`${TranscriptionMessagesKey}`,
		fetchTranscription,
		CacheKey.Transcription,
		true,
		undefined,
		interactionId,
		"always"
	);

	useEffect(() => {
		if (date) {
			setFilter(date);
		}
	}, [date]);

	const apiMessages = useMemo<CommonChatMessage[]>(
		() => transcriptionMessagesResult.data?.pages.flatMap((w) => w.items) ?? [],
		[transcriptionMessagesResult, transcriptionMessagesInView]
	);

	const intl = useIntl();

	const defaultCustomerChatNameTranslated = intl.formatMessage({
		id: "Interaction.WebchatDefaultCustomerChatName",
	});

	const classes = useChatStyle();

	const isChatMessage = (
		msg: ChatMessage | CustomMessage
	): msg is ChatMessage => {
		return (msg as ChatMessage).senderId !== undefined;
	};

	const transcriptionMessages: (
		| ExtendedChatMessage
		| CustomMessage
		| SystemMessage
	)[] = useMemo(() => {
		const reversedApiMessages = [...apiMessages].reverse();

		const messages: (ExtendedChatMessage | CustomMessage | SystemMessage)[] =
			reversedApiMessages.map((m, i) => {
				if (!m.senderName) {
					return {
						messageType: DisplayMessageType.System,
						messageId: m.id,
						createdOn: new Date(m.timestamp),
						content: intl.formatMessage({
							id: `Interaction.Transcription${m.body}`,
						}),
						showStatus: false,
						systemMessageType: "content",
						iconName: "Info",
					};
				}

				const nextMessage = reversedApiMessages[i - 1];
				const isDifferentSource = nextMessage?.creatorId !== m.creatorId;

				const isTimeGap = nextMessage
					? dayjs(m.timestamp).diff(dayjs(nextMessage.timestamp), "minutes") > 5
					: false;
				const attached = !isDifferentSource && !isTimeGap;
				const mine = m.creatorId !== null;

				return {
					messageType: DisplayMessageType.Chat,
					senderId: m.creatorId,
					senderDisplayName:
						m.senderName == WebchatDefaultCustomerName
							? defaultCustomerChatNameTranslated
							: m.senderName,
					messageId: m.id,
					content: m.body,
					createdOn: new Date(m.timestamp),
					mine: mine,
					attached: attached,
					contentType: "html",
					showStatus: false,
					attachments: [],
				};
			});

		if (!!transcriptionMessagesResult.hasNextPage) {
			messages.unshift({
				messageType: "custom",
				content: "spinner",
				messageId: "loading",
				createdOn: new Date(),
			});
		}

		return messages;
	}, [apiMessages]);

	const onRenderMessage = (
		messageProps: MessageProps,
		defaultOnRender?: MessageRenderer
	): JSX.Element => {
		const { message } = messageProps as unknown as {
			message: ExtendedChatMessage | ExtendedCustomMessage;
		};

		if (!isChatMessage(message) && !!transcriptionMessagesResult.hasNextPage) {
			return (
				<Spinner
					ref={transcriptionMessagesInView.ref}
					size="tiny"
					label={intl.formatMessage({ id: "Common.Loading" })}
				/>
			);
		}

		const chatMessage = message as ChatMessage;
		const isMine = chatMessage.senderId !== null;
		const senderDisplayName = chatMessage.senderDisplayName;

		return (
			<div
				title={isMine ? senderDisplayName : ""}
				className={isMine ? classes.sentMessage : ""}
			>
				{defaultOnRender ? defaultOnRender(messageProps) : <></>}
				{isMine && message.showStatus && message.statusElement}
				{isMine && !message.showStatus && (
					<div style={{ width: "30px", display: "flex", flexShrink: 0 }}></div>
				)}
			</div>
		);
	};

	const sendMessages = async () => {
		return;
	};

	const onRenderAttachments = () => {
		return <></>;
	};

	return {
		chatMessages: transcriptionMessages,
		apiMessages,
		onRenderMessage,
		sendMessages,
		onRenderAttachments,
	};
};
